import { useI18n } from '@/hooks/useI18n';
import { useState } from 'react';
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Delta } from '@/components/primitives/Delta';
import type { IdentityPerformance, IdentityResponse } from '@/types/api';
import { useDeleteIdentity } from '@/hooks/mutations/useIdentityCrud';
import { formatNumber } from '@/lib/format';
import { IdentityHealthPopover } from './IdentityHealthPopover';

interface IdentityRowProps {
  identity: IdentityResponse;
  perf: IdentityPerformance | null;
  onEdit: () => void;
}

export function IdentityRow({ identity, perf, onEdit }: IdentityRowProps) {
  const { t } = useI18n();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const del = useDeleteIdentity();
  const color = identity.color ?? 'hsl(var(--text-3))';

  const health = perf?.healthScore ?? null;
  const healthTone =
    health === null
      ? 'text-text-4'
      : health >= 70
        ? 'text-emerald'
        : health >= 40
          ? 'text-amber'
          : 'text-rose';

  return (
    <>
      <div className="group grid grid-cols-[minmax(0,1fr)_44px] items-center gap-3 px-4 py-3 transition-colors hover:bg-bg-2/70 lg:min-h-16 lg:grid-cols-[minmax(0,1fr)_auto_28px] lg:gap-4 lg:py-2">
        <div className="flex min-w-0 items-center gap-3">
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ background: color, boxShadow: `0 0 0 3px ${color}18` }}
          />
          <div className="min-w-0">
            <div className="break-words text-[13px] font-semibold text-text-1 lg:truncate">
              {identity.name}
            </div>
            {identity.description && (
              <div className="break-words text-[11px] text-text-3 lg:truncate">
                {identity.description}
              </div>
            )}
          </div>
        </div>
        <div className="col-span-2 row-start-2 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-line-1 pt-3 lg:col-span-1 lg:col-start-2 lg:row-start-1 lg:flex lg:items-center lg:gap-4 lg:border-0 lg:pt-0">
          <span className="text-[12px] tabular-nums text-text-2 lg:w-20 lg:text-right lg:text-[11px] lg:text-text-3">
            <span className="mb-1 block text-[10px] text-text-3 lg:sr-only">
              {t('Comptes', 'Accounts')}
            </span>
            {identity.accountCount}
            <span className="hidden lg:inline"> {t('comptes', 'accounts')}</span>
          </span>
          <span className="text-[12px] tabular-nums text-text-2 lg:w-28 lg:text-right">
            <span className="mb-1 block text-[10px] text-text-3 lg:sr-only">
              {t('Abonnés', 'Followers')}
            </span>
            {perf ? formatNumber(perf.totalFollowers) : '—'}
            <span className="ml-1 hidden text-text-4 lg:inline">{t('abonnés', 'followers')}</span>
          </span>
          <span className="lg:w-16 lg:text-right">
            <span className="mb-1 block text-[10px] text-text-3 lg:sr-only">
              {t('Croissance', 'Growth')}
            </span>
            {perf?.avgFollowerGrowthPct !== undefined && perf !== null ? (
              <Delta value={perf.avgFollowerGrowthPct} format="pct" />
            ) : (
              <span className="font-mono text-[11px] text-text-4">—</span>
            )}
          </span>
          <span
            aria-label={t('Score de santé', 'Health score')}
            className={`text-[12px] tabular-nums lg:w-28 lg:text-right ${healthTone}`}
          >
            <IdentityHealthPopover
              name={identity.name}
              score={health}
              details={perf?.healthDetails}
            >
              <span>
                <span className="mb-1 block text-[10px] text-text-3 lg:sr-only">
                  {t('Santé', 'Health')}
                </span>
                {health !== null
                  ? `${Math.round(health)}/100`
                  : t('Non évaluable', 'Not enough data')}
              </span>
            </IdentityHealthPopover>
          </span>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="col-start-2 row-start-1 h-11 w-11 lg:col-start-3 lg:h-7 lg:w-7"
              aria-label={t(`Actions pour ${identity.name}`, `Actions for ${identity.name}`)}
            >
              <MoreHorizontal className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onEdit}>
              <Pencil className="mr-2 h-3.5 w-3.5" />
              {t('Modifier', 'Edit')}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-rose focus:text-rose"
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="mr-2 h-3.5 w-3.5" />
              {t('Supprimer', 'Delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t(`Supprimer « ${identity.name} » ?`, `Delete “${identity.name}”?`)}
            </DialogTitle>
            <DialogDescription>
              {identity.accountCount > 0
                ? t(
                    `Impossible : ${identity.accountCount} compte${identity.accountCount > 1 ? 's sont rattachés' : ' est rattaché'} à cette identité.`,
                    `Cannot delete: ${identity.accountCount} account${identity.accountCount > 1 ? 's are' : ' is'} linked to this identity.`,
                  )
                : t('Cette action est irréversible.', 'This action cannot be undone.')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
              {t('Annuler', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={identity.accountCount > 0 || del.isPending}
              onClick={() =>
                del.mutate(identity.id, {
                  onSuccess: () => setConfirmDelete(false),
                })
              }
            >
              {t('Supprimer', 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
